import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import {
  getAdminOverview,
  listAdminUsers,
  getAdminUsage,
  getAdminEmailHealth,
  setUserAdminRole,
  setLifetimeComp,
  previewAuthEmail,
  previewTransactionalEmails,
} from "@/lib/admin.functions";

import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Shield,
  Users,
  BarChart3,
  Mail,
  CreditCard,
  Loader2,
  Eye,
  Smartphone,
  Monitor,
  AlertCircle,
} from "lucide-react";

export const Route = createFileRoute("/app/admin")({
  head: () => ({
    meta: buildAppMeta({
      title: "Admin — CoachQuick",
      description:
        "Internal admin tools for CoachQuick staff to manage users, billing, and platform health.",
      path: "/app/admin",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/admin` }],
  }),
  component: () => (
    <RequireAuth>
      <AdminGate />
    </RequireAuth>
  ),
});

function AdminGate() {
  const isAdmin = useIsAdmin();
  const nav = useNavigate();
  useEffect(() => {
    // Allow a tick for the role check before redirecting
    const t = setTimeout(() => {
      if (!isAdmin) nav({ to: "/app" });
    }, 600);
    return () => clearTimeout(t);
  }, [isAdmin, nav]);
  if (!isAdmin) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying access…
        </div>
      </AppShell>
    );
  }
  return (
    <AppShell>
      <AdminPage />
    </AppShell>
  );
}

function fmtMoney(cents: number) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(
    cents / 100,
  );
}

function AdminPage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-brand-text" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin</h1>
          <p className="text-sm text-muted-foreground">CoachQuick operator console.</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="overview">
            <BarChart3 className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="mr-2 h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="subscriptions">
            <CreditCard className="mr-2 h-4 w-4" />
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="usage">
            <BarChart3 className="mr-2 h-4 w-4" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="mr-2 h-4 w-4" />
            Email
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab />
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <UsersTab />
        </TabsContent>
        <TabsContent value="subscriptions" className="mt-4">
          <SubscriptionsTab />
        </TabsContent>
        <TabsContent value="usage" className="mt-4">
          <UsageTab />
        </TabsContent>
        <TabsContent value="email" className="mt-4">
          <EmailTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
    </Card>
  );
}

function OverviewTab() {
  const fn = useServerFn(getAdminOverview);
  const { data, isLoading } = useQuery({ queryKey: ["admin-overview"], queryFn: () => fn() });
  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi label="Coaches" value={data.coaches} />
      <Kpi label="Active subs" value={data.subs.active} hint={`of ${data.subs.total}`} />
      <Kpi label="Trialing" value={data.subs.trialing} />
      <Kpi label="Past due (grace)" value={data.subs.pastDue} />
      <Kpi label="Locked" value={data.subs.locked} />
      <Kpi label="MRR (est.)" value={fmtMoney(data.mrrCents)} />
      <Kpi label="Signups · 7d" value={data.signups7} hint={`${data.signups30} in 30d`} />
      <Kpi label="Sessions · 7d" value={data.sessions7} hint={`${data.sessions30} in 30d`} />
      <Kpi label="Emails · 30d" value={data.emails30} />
      <Kpi label="Library items" value={data.drills} />
      <Kpi label="Clients" value={data.clients} />
      <Kpi label="Invoices" value={data.invoices} />
    </div>
  );
}

function UsersTab() {
  const fn = useServerFn(listAdminUsers);
  const setRole = useServerFn(setUserAdminRole);
  const setComp = useServerFn(setLifetimeComp);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: () => fn() });
  const [q, setQ] = useState("");
  const [busyComp, setBusyComp] = useState<string | null>(null);

  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const filtered = data.filter(
    (u) =>
      !q ||
      (u.full_name ?? "").toLowerCase().includes(q.toLowerCase()) ||
      (u.email ?? "").toLowerCase().includes(q.toLowerCase()) ||
      u.id.toLowerCase().includes(q.toLowerCase()),
  );

  async function toggleAdmin(user_id: string, makeAdmin: boolean) {
    try {
      await setRole({ data: { user_id, make_admin: makeAdmin } });
      toast.success(makeAdmin ? "Promoted to admin" : "Admin removed");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }

  async function toggleComp(coach_id: string, enabled: boolean) {
    setBusyComp(coach_id);
    try {
      const reason = enabled
        ? (window.prompt("Optional note (e.g. 'beta tester', 'lifetime gift'):") ?? null)
        : null;
      await setComp({ data: { coach_id, enabled, reason } });
      toast.success(enabled ? "Lifetime Pro granted" : "Lifetime Pro revoked");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["my-subscription"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusyComp(null);
    }
  }

  return (
    <div className="space-y-3">
      <Input
        placeholder="Search by name, email, or user id"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="max-w-sm"
      />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Roles</th>
                <th className="px-3 py-2">Subscription</th>
                <th className="px-3 py-2">Comp</th>
                <th className="px-3 py-2">Joined</th>
                <th className="px-3 py-2">Last session</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isAdmin = u.roles.includes("admin");
                const isComped = !!u.subscription?.lifetime_comp;
                return (
                  <tr key={u.id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{u.full_name ?? "—"}</div>
                      <div className="text-xs text-muted-foreground" title={u.id}>
                        {u.email ?? u.id}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <Badge key={r} variant={r === "admin" ? "default" : "secondary"}>
                            {r}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {isComped ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600">Comp · Clinics</Badge>
                      ) : u.subscription ? (
                        <span className="capitalize">{u.subscription.status}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isComped}
                          disabled={busyComp === u.id}
                          onCheckedChange={(v) => toggleComp(u.id, v)}
                          aria-label="Lifetime Clinics comp"
                        />
                        {isComped && u.subscription?.comp_reason ? (
                          <span
                            className="text-xs text-muted-foreground truncate max-w-[140px]"
                            title={u.subscription.comp_reason}
                          >
                            {u.subscription.comp_reason}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {u.last_session_at ? new Date(u.last_session_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant={isAdmin ? "outline" : "default"}
                        onClick={() => toggleAdmin(u.id, !isAdmin)}
                      >
                        {isAdmin ? "Remove admin" : "Make admin"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SubscriptionsTab() {
  const fn = useServerFn(listAdminUsers);
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: () => fn() });
  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const rows = data.filter((u) => u.subscription);
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Coach</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Trial ends</th>
              <th className="px-3 py-2">Period ends</th>
              <th className="px-3 py-2">Cancel at period end</th>
              <th className="px-3 py-2">Stripe customer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="px-3 py-2">
                  <div className="font-medium">{u.full_name ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">{u.id}</div>
                </td>
                <td className="px-3 py-2 capitalize">{u.subscription!.status}</td>
                <td className="px-3 py-2">
                  {u.subscription!.trial_ends_at
                    ? new Date(u.subscription!.trial_ends_at).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-3 py-2">
                  {u.subscription!.current_period_end
                    ? new Date(u.subscription!.current_period_end).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-3 py-2">{u.subscription!.cancel_at_period_end ? "Yes" : "No"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {u.subscription!.stripe_customer_id ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function UsageTab() {
  const fn = useServerFn(getAdminUsage);
  const { data, isLoading } = useQuery({ queryKey: ["admin-usage"], queryFn: () => fn() });
  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const maxDay = Math.max(1, ...data.days.map((d) => d.count));
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="p-4">
        <h3 className="text-sm font-semibold">Sessions per day · 30d</h3>
        <div className="mt-3 flex h-32 items-end gap-1">
          {data.days.map((d) => (
            <div key={d.day} className="flex-1" title={`${d.day}: ${d.count}`}>
              <div
                className="w-full rounded-t bg-primary/70"
                style={{ height: `${(d.count / maxDay) * 100}%` }}
              />
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4">
        <h3 className="text-sm font-semibold">Feature usage · 30d</h3>
        <ul className="mt-3 space-y-2">
          {data.features.map((f) => (
            <li key={f.feature} className="flex items-center justify-between text-sm">
              <span>{f.feature}</span>
              <span className="font-mono">{f.count}</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-4 md:col-span-2">
        <h3 className="text-sm font-semibold">Top coaches by sessions · 30d</h3>
        <ul className="mt-3 divide-y divide-border">
          {data.topCoaches.map((c) => (
            <li key={c.coach_id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <div className="font-medium">{c.name ?? "—"}</div>
                <div className="text-xs text-muted-foreground">{c.coach_id}</div>
              </div>
              <div className="font-mono">{c.count}</div>
            </li>
          ))}
          {data.topCoaches.length === 0 && (
            <li className="py-2 text-sm text-muted-foreground">No sessions in this window.</li>
          )}
        </ul>
      </Card>
    </div>
  );
}

function EmailTab() {
  const fnHealth = useServerFn(getAdminEmailHealth);
  const fnTransactional = useServerFn(previewTransactionalEmails);
  const fnAuth = useServerFn(previewAuthEmail);
  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ["admin-email"],
    queryFn: () => fnHealth(),
  });
  const { data: transactional, isLoading: transactionalLoading } = useQuery({
    queryKey: ["admin-email-templates"],
    queryFn: () => fnTransactional(),
  });
  const [activeSubTab, setActiveSubTab] = useState("health");
  const [selectedAuth, setSelectedAuth] = useState<string | null>("signup");
  const [selectedTransactional, setSelectedTransactional] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);

  const { data: authPreview, isLoading: authLoading } = useQuery({
    queryKey: ["admin-email-auth", selectedAuth],
    queryFn: () => fnAuth({ data: { type: selectedAuth! } }),
    enabled: activeSubTab === "templates" && selectedAuth !== null,
  });

  const selectedTransactionalPreview = transactional?.find(
    (t) => t.templateName === selectedTransactional,
  );

  return (
    <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
      <TabsList className="mb-4">
        <TabsTrigger value="health">
          <BarChart3 className="mr-2 h-4 w-4" />
          Health
        </TabsTrigger>
        <TabsTrigger value="templates">
          <Eye className="mr-2 h-4 w-4" />
          Templates
        </TabsTrigger>
      </TabsList>

      <TabsContent value="health">
        {healthLoading || !health ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-4">
              <h3 className="text-sm font-semibold">Status · 30d</h3>
              <ul className="mt-3 space-y-2">
                {health.statuses.map((s) => (
                  <li key={s.status} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{s.status}</span>
                    <span className="font-mono">{s.count}</span>
                  </li>
                ))}
                {health.statuses.length === 0 && (
                  <li className="text-sm text-muted-foreground">No email activity.</li>
                )}
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="text-sm font-semibold">Top templates · 30d</h3>
              <ul className="mt-3 space-y-2">
                {health.templates.map((t) => (
                  <li key={t.template} className="flex items-center justify-between text-sm">
                    <span>{t.template}</span>
                    <span className="font-mono">{t.count}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-4 md:col-span-2">
              <div className="flex items-center justify-between text-sm">
                <span>Suppressed emails</span>
                <span className="font-mono">{health.suppressed}</span>
              </div>
            </Card>
          </div>
        )}
      </TabsContent>

      <TabsContent value="templates">
        <Tabs defaultValue="transactional">
          <TabsList className="mb-4">
            <TabsTrigger value="transactional">
              Transactional ({transactional?.length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="auth">Auth (6)</TabsTrigger>
          </TabsList>

          <TabsContent value="transactional">
            {transactionalLoading ? (
              <div className="text-sm text-muted-foreground">Loading templates…</div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
                <Card className="max-h-[600px] overflow-auto p-0">
                  <div className="divide-y">
                    {transactional?.map((t) => (
                      <button
                        key={t.templateName}
                        onClick={() => setSelectedTransactional(t.templateName)}
                        className={`w-full px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                          selectedTransactional === t.templateName ? "bg-muted" : ""
                        }`}
                      >
                        <div className="text-sm font-medium">{t.displayName}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {t.status === "ready" ? t.subject : t.status}
                        </div>
                        {t.status !== "ready" && (
                          <Badge
                            variant={t.status === "render_failed" ? "destructive" : "secondary"}
                            className="mt-1 text-[10px]"
                          >
                            {t.status}
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {selectedTransactionalPreview
                        ? selectedTransactionalPreview.displayName
                        : "Select a template"}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={mobile ? "ghost" : "secondary"}
                        size="icon"
                        onClick={() => setMobile(false)}
                        title="Desktop"
                      >
                        <Monitor className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={mobile ? "secondary" : "ghost"}
                        size="icon"
                        onClick={() => setMobile(true)}
                        title="Mobile"
                      >
                        <Smartphone className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {selectedTransactionalPreview ? (
                    selectedTransactionalPreview.status === "ready" ? (
                      <iframe
                        srcDoc={selectedTransactionalPreview.html}
                        title={selectedTransactionalPreview.displayName}
                        className="w-full rounded border border-border bg-white"
                        style={{
                          height: "600px",
                          maxWidth: mobile ? "375px" : "100%",
                          margin: "0 auto",
                        }}
                        sandbox=""
                      />
                    ) : (
                      <div className="flex items-center gap-2 rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        {selectedTransactionalPreview.errorMessage || "Preview data required"}
                      </div>
                    )
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Choose a template from the list to preview.
                    </div>
                  )}
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="auth">
            <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
              <Card className="max-h-[600px] overflow-auto p-0">
                <div className="divide-y">
                  {[
                    { key: "signup", label: "Signup confirmation" },
                    { key: "invite", label: "Invite" },
                    { key: "magiclink", label: "Magic link" },
                    { key: "recovery", label: "Password reset" },
                    { key: "email_change", label: "Email change" },
                    { key: "reauthentication", label: "Reauthentication" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setSelectedAuth(key)}
                      className={`w-full px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                        selectedAuth === key ? "bg-muted" : ""
                      }`}
                    >
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs text-muted-foreground">{key}</div>
                    </button>
                  ))}
                </div>
              </Card>

              <Card className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-semibold">
                    {authPreview ? authPreview.displayName : "Select an auth email"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={mobile ? "ghost" : "secondary"}
                      size="icon"
                      onClick={() => setMobile(false)}
                      title="Desktop"
                    >
                      <Monitor className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={mobile ? "secondary" : "ghost"}
                      size="icon"
                      onClick={() => setMobile(true)}
                      title="Mobile"
                    >
                      <Smartphone className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {authLoading ? (
                  <div className="text-sm text-muted-foreground">Loading preview…</div>
                ) : authPreview ? (
                  authPreview.status === "ready" ? (
                    <iframe
                      srcDoc={authPreview.html}
                      title={authPreview.displayName}
                      className="w-full rounded border border-border bg-white"
                      style={{
                        height: "600px",
                        maxWidth: mobile ? "375px" : "100%",
                        margin: "0 auto",
                      }}
                      sandbox=""
                    />
                  ) : (
                    <div className="flex items-center gap-2 rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {authPreview.errorMessage || "Could not render preview"}
                    </div>
                  )
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Choose an auth email from the list to preview.
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </TabsContent>
    </Tabs>
  );
}
